import i18n from '@/utils/i18n';
import { GET_ESSENTIAL_ITEMS_QUERY } from '@/contexts/variant-pack-info-context-queries';
import { getCodeFromGQLError } from '@/utils/apollo-client';
import { gql, useMutation } from '@apollo/client';
import {
  ORDER_GUIDE_ITEM_FRAGMENT,
  ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT,
} from '@/contexts/items-context-queries';
import {
  OrderGuide_CreatePartitionItemMutation,
  OrderGuide_CreatePartitionItemMutationVariables,
  OrderGuide_DeletePartitionItemMutation,
  OrderGuide_DeletePartitionItemMutationVariables,
  OrderGuide_UpdateEssentialItemsMutation,
  OrderGuide_UpdateEssentialItemsMutationVariables,
  OrderGuide_UpdateOrderGuideItemDispositionsMutation,
  OrderGuide_UpdateOrderGuideItemDispositionsMutationVariables,
  OrderGuide_UpdateOrderGuideItemNotesMutation,
  OrderGuide_UpdateOrderGuideItemNotesMutationVariables,
  UpdateOrderGuideItemDispositionsItemInput,
  VariantPackInfoContext_GetEssentialItemsQuery,
  VariantPackInfoContext_GetEssentialItemsQueryVariables,
} from '@/types';
import { OrderGuideErrorCode } from '@/enums';
import { Type } from '@/components/toast';
import { useCallback, useMemo } from 'react';
import { useOrderGuideItemQueryVariables } from './use-item-query-variables';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useOrderGuideStoreApi } from '@/stores/order-guide-store';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';

export const ADD_TO_ORDER_GUIDE_MUTATION = gql`
  mutation OrderGuide_CreatePartitionItem(
    $locale: String!
    $partitionUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPackUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    createOrderGuidePartitionItem(
      partition_id: $partitionUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_id: $variantPackUUID
      is_request: false
    ) {
      ...ItemsContext_OrderGuideItemFields
      order_guide_restaurant_partition {
        order_guide_restaurant_collection {
          ...ItemsContext_OrderGuideRestaurantCollectionFields
        }
        uuid
      }
    }
  }

  ${ORDER_GUIDE_ITEM_FRAGMENT}
  ${ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT}
`;

export const REMOVE_FROM_ORDER_GUIDE_MUTATION = gql`
  mutation OrderGuide_DeletePartitionItem(
    $partitionUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPackUUID: uuid!
  ) {
    deleteOrderGuidePartitionItem(
      partition_id: $partitionUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_id: $variantPackUUID
    ) {
      success
    }
  }
`;

export const UPDATE_ORDER_GUIDE_ITEM_DISPOSITIONS_MUTATION = gql`
  mutation OrderGuide_UpdateOrderGuideItemDispositions(
    $locale: String!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $orderGuideItems: [UpdateOrderGuideItemDispositionsItemInput!]!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    updateOrderGuideItemDispositions(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      order_guide_items: $orderGuideItems
    ) {
      ...ItemsContext_OrderGuideItemFields
      order_guide_restaurant_partition {
        order_guide_restaurant_collection {
          ...ItemsContext_OrderGuideRestaurantCollectionFields
        }
        uuid
      }
    }
  }

  ${ORDER_GUIDE_ITEM_FRAGMENT}
  ${ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT}
`;

export const UPDATE_ORDER_GUIDE_ITEM_NOTES_MUTATION = gql`
  mutation OrderGuide_UpdateOrderGuideItemNotes(
    $locale: String!
    $notes: String
    $partitionUUID: uuid!
    $persistNotes: Boolean
    $isRequest: Boolean
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPackUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    updateOrderGuideItemNotes(
      notes: $notes
      partition_id: $partitionUUID
      persist_notes: $persistNotes
      is_request: $isRequest
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_id: $variantPackUUID
    ) {
      ...ItemsContext_OrderGuideItemFields
      order_guide_restaurant_partition {
        order_guide_restaurant_collection {
          ...ItemsContext_OrderGuideRestaurantCollectionFields
        }
        uuid
      }
    }
  }

  ${ORDER_GUIDE_ITEM_FRAGMENT}
  ${ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT}
`;

export const UPDATE_ESSENTIAL_ITEMS_MUTATION = gql`
  mutation OrderGuide_UpdateEssentialItems(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $addedVariantPackUUIDs: [uuid!]!
    $removedVariantPackUUIDs: [uuid!]!
    $skipAddMutation: Boolean!
    $skipRemoveMutation: Boolean!
  ) {
    createCriticalItems(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_id_list: $addedVariantPackUUIDs
    ) @skip(if: $skipAddMutation) {
      variant_pack_id
    }

    deleteCriticalItems(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_id_list: $removedVariantPackUUIDs
    ) @skip(if: $skipRemoveMutation) {
      success
    }
  }
`;

interface UpdateNotePayload {
  isRequest?: boolean;
  notes: string | null;
  orderGuideItemUUID: string;
  partitionUUID: string;
  persistNotes: boolean;
  variantPackUUID: string;
}

export const useMultiOrderGuideMutations = () => {
  const itemQueryVariables = useOrderGuideItemQueryVariables();
  const orderGuideStoreApi = useOrderGuideStoreApi();
  const { selectedRestaurantRelationship } = useUserContext();
  const { refetch, selectedCollection } = useOrderGuideItemsContext();
  const { showToast } = useToasts();

  // Reloads the order guide when the server says it's stale, e.g. when
  // another user updated it after it was loaded
  const reloadIfStale = useCallback(
    async (error: unknown) => {
      if (getCodeFromGQLError(error) !== OrderGuideErrorCode.STALE) {
        throw error;
      }

      await refetch();
      showToast({
        content: i18n.t('toast.order_guide_reloaded'),
        type: Type.WARNING,
      });

      return { data: undefined };
    },
    [refetch, showToast],
  );

  const [addToOrderGuide] = useMutation<
    OrderGuide_CreatePartitionItemMutation,
    OrderGuide_CreatePartitionItemMutationVariables
  >(ADD_TO_ORDER_GUIDE_MUTATION);

  const [removeFromOrderGuide] = useMutation<
    OrderGuide_DeletePartitionItemMutation,
    OrderGuide_DeletePartitionItemMutationVariables
  >(REMOVE_FROM_ORDER_GUIDE_MUTATION);

  const [updateOrderGuideItemDispositions] = useMutation<
    OrderGuide_UpdateOrderGuideItemDispositionsMutation,
    OrderGuide_UpdateOrderGuideItemDispositionsMutationVariables
  >(UPDATE_ORDER_GUIDE_ITEM_DISPOSITIONS_MUTATION, {
    context: { ignoreGraphQLErrorCodes: [OrderGuideErrorCode.STALE] },
  });

  const [updateOrderGuideItemNotes] = useMutation<
    OrderGuide_UpdateOrderGuideItemNotesMutation,
    OrderGuide_UpdateOrderGuideItemNotesMutationVariables
  >(UPDATE_ORDER_GUIDE_ITEM_NOTES_MUTATION, {
    context: { ignoreGraphQLErrorCodes: [OrderGuideErrorCode.STALE] },
  });

  const [updateEssentialItems] = useMutation<
    OrderGuide_UpdateEssentialItemsMutation,
    OrderGuide_UpdateEssentialItemsMutationVariables
  >(UPDATE_ESSENTIAL_ITEMS_MUTATION);

  const addToOrderGuideCallback = useCallback(
    async (partitionUUID: string, variantPackUUID: string) => {
      const { data } = await addToOrderGuide({
        variables: {
          ...itemQueryVariables,
          partitionUUID,
          restaurantUUID: selectedRestaurantRelationship.uuid,
          supplierUUID: selectedRestaurantRelationship.supplierUUID,
          variantPackUUID,
        },
      });

      const orderGuideItem = data?.createOrderGuidePartitionItem;
      if (orderGuideItem) {
        orderGuideStoreApi.getState().addItem(orderGuideItem);

        if (orderGuideItem.order_guide_restaurant_partition) {
          const collection =
            orderGuideItem.order_guide_restaurant_partition
              .order_guide_restaurant_collection;
          if (collection) {
            orderGuideStoreApi
              .getState()
              .addRestaurantCollectionOnce(collection);
          }
        }
      }
    },
    [
      addToOrderGuide,
      itemQueryVariables,
      orderGuideStoreApi,
      selectedRestaurantRelationship,
    ],
  );

  const removeFromOrderGuideCallback = useCallback(
    async (partitionUUID: string, variantPackUUID: string) => {
      const store = orderGuideStoreApi.getState();
      const snapshot = store.snapshot();

      // Optimistic update - remove item immediately
      store.removeItem(variantPackUUID, partitionUUID);

      try {
        await removeFromOrderGuide({
          variables: {
            partitionUUID,
            restaurantUUID: selectedRestaurantRelationship.uuid,
            supplierUUID: selectedRestaurantRelationship.supplierUUID,
            variantPackUUID,
          },
        });
      } catch (error) {
        // Rollback on error
        store.restore(snapshot);
        throw error;
      }
    },
    [orderGuideStoreApi, removeFromOrderGuide, selectedRestaurantRelationship],
  );

  const updateOrderGuideItemDispositionsCallback = useCallback(
    async (orderGuideItems: UpdateOrderGuideItemDispositionsItemInput[]) => {
      const defaultPartitionUUID = selectedCollection?.defaultPartitionUUID;
      if (!defaultPartitionUUID) {
        return;
      }

      const { data } = await updateOrderGuideItemDispositions({
        variables: {
          ...itemQueryVariables,
          orderGuideItems,
          restaurantUUID: selectedRestaurantRelationship.uuid,
          supplierUUID: selectedRestaurantRelationship.supplierUUID,
        },
      }).catch(reloadIfStale);

      const updateResponse = data?.updateOrderGuideItemDispositions;
      if (!updateResponse) {
        return;
      }

      // Build map of variant pack UUID to set of partition UUIDs that were updated
      const variantPackToPartitionsMap = orderGuideItems.reduce<{
        [variantPackUUID: string]: Set<string>;
      }>((acc, ogi) => {
        const variantPackUUID = ogi.variant_pack_id;
        const partitionUUID = ogi.partition_id;
        if (!variantPackUUID || !partitionUUID) {
          return acc;
        }
        const vpPartitionUUIDs = acc[variantPackUUID] || new Set();
        vpPartitionUUIDs.add(partitionUUID);
        return {
          ...acc,
          [variantPackUUID]: vpPartitionUUIDs,
        };
      }, {});

      // Update the store with the new dispositions
      orderGuideStoreApi
        .getState()
        .updateItemDispositions(
          updateResponse,
          variantPackToPartitionsMap,
          defaultPartitionUUID,
        );

      for (const updatedItem of updateResponse) {
        if (updatedItem.order_guide_restaurant_partition) {
          orderGuideStoreApi
            .getState()
            .addRestaurantCollectionOnce(
              updatedItem.order_guide_restaurant_partition
                .order_guide_restaurant_collection,
            );
        }
      }
    },
    [
      itemQueryVariables,
      orderGuideStoreApi,
      reloadIfStale,
      selectedCollection,
      selectedRestaurantRelationship,
      updateOrderGuideItemDispositions,
    ],
  );

  const updateOrderGuideItemNotesCallback = useCallback(
    async ({
      notes,
      orderGuideItemUUID,
      partitionUUID,
      persistNotes,
      isRequest,
      variantPackUUID,
    }: UpdateNotePayload) => {
      const { data } = await updateOrderGuideItemNotes({
        variables: {
          ...itemQueryVariables,
          isRequest,
          notes,
          partitionUUID,
          persistNotes,
          restaurantUUID: selectedRestaurantRelationship.uuid,
          supplierUUID: selectedRestaurantRelationship.supplierUUID,
          variantPackUUID,
        },
      }).catch(reloadIfStale);

      const updatedItem = data?.updateOrderGuideItemNotes;
      if (updatedItem) {
        // Update the item in the store
        // Note: the order_guide_item_id may change if the item was an OGS and is now an OGR
        orderGuideStoreApi
          .getState()
          .updateItem(orderGuideItemUUID, updatedItem);

        if (updatedItem.order_guide_restaurant_partition) {
          const collection =
            updatedItem.order_guide_restaurant_partition
              .order_guide_restaurant_collection;
          if (collection) {
            orderGuideStoreApi
              .getState()
              .addRestaurantCollectionOnce(collection);
          }
        }

        return true;
      }

      // Return false to inform callers that notes weren't saved
      return false;
    },
    [
      itemQueryVariables,
      orderGuideStoreApi,
      reloadIfStale,
      selectedRestaurantRelationship,
      updateOrderGuideItemNotes,
    ],
  );

  const updateEssentialItemsCallback = useCallback(
    async (
      addedVariantPackUUIDs: string[],
      removedVariantPackUUIDs: string[],
    ) => {
      await updateEssentialItems({
        update: (client, result) => {
          const cachedEssentialItems = client.readQuery<
            VariantPackInfoContext_GetEssentialItemsQuery,
            VariantPackInfoContext_GetEssentialItemsQueryVariables
          >({
            query: GET_ESSENTIAL_ITEMS_QUERY,
            variables: {
              restaurantUUID: selectedRestaurantRelationship.uuid,
              supplierUUID: selectedRestaurantRelationship.supplierUUID,
            },
          });
          const newEssentialItems = result.data?.createCriticalItems || [];
          const removedUUIDS = result.data?.deleteCriticalItems?.success
            ? removedVariantPackUUIDs
            : [];
          const existingEssentialItems = cachedEssentialItems?.getCriticalItems;
          if (existingEssentialItems) {
            const nextEssentialItems = [
              ...existingEssentialItems,
              ...newEssentialItems,
            ].filter((vpResponse) => {
              return !removedUUIDS.includes(vpResponse.variant_pack_id);
            });
            client.writeQuery<
              VariantPackInfoContext_GetEssentialItemsQuery,
              VariantPackInfoContext_GetEssentialItemsQueryVariables
            >({
              data: {
                getCriticalItems: nextEssentialItems,
              },
              query: GET_ESSENTIAL_ITEMS_QUERY,
              variables: {
                restaurantUUID: selectedRestaurantRelationship.uuid,
                supplierUUID: selectedRestaurantRelationship.supplierUUID,
              },
            });
          }
        },
        variables: {
          addedVariantPackUUIDs,
          removedVariantPackUUIDs,
          restaurantUUID: selectedRestaurantRelationship.uuid,
          skipAddMutation: addedVariantPackUUIDs.length === 0,
          skipRemoveMutation: removedVariantPackUUIDs.length === 0,
          supplierUUID: selectedRestaurantRelationship.supplierUUID,
        },
      });
    },
    [
      selectedRestaurantRelationship.supplierUUID,
      selectedRestaurantRelationship.uuid,
      updateEssentialItems,
    ],
  );

  return useMemo(
    () => ({
      addToOrderGuide: addToOrderGuideCallback,
      removeFromOrderGuide: removeFromOrderGuideCallback,
      updateEssentialItems: updateEssentialItemsCallback,
      updateItemDispositions: updateOrderGuideItemDispositionsCallback,
      updateItemNotes: updateOrderGuideItemNotesCallback,
    }),
    [
      addToOrderGuideCallback,
      removeFromOrderGuideCallback,
      updateEssentialItemsCallback,
      updateOrderGuideItemDispositionsCallback,
      updateOrderGuideItemNotesCallback,
    ],
  );
};
